import React, { useState } from 'react';
import './DashboardView.css';
import {
  BarChart, Bar,
  AreaChart, Area,
  PieChart, Pie, Cell,
  XAxis, YAxis,
  CartesianGrid, Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

// ─── Static chart data ────────────────────────────────────────────────────────
const registrationData = [
  { date: 'Apr 21', users: 10200, createUp: 12400 },
  { date: 'Apr 22', users: 13500, createUp: 14800 },
  { date: 'Apr 23', users: 12100, createUp: 13600 },
  { date: 'Apr 24', users: 16800, createUp: 18200 },
  { date: 'Apr 25', users: 19400, createUp: 21000 },
  { date: 'Apr 26', users: 22700, createUp: 24500 },
  { date: 'Apr 27', users: 27300, createUp: 29800 },
  { date: 'Apr 28', users: 31000, createUp: 33200 },
];

const onlineData = [
  { time: 'Apr 29', users: 8500 },
  { time: 'Apr 30', users: 9100 },
  { time: 'May 1',  users: 8800 },
  { time: 'May 2',  users: 9600 },
  { time: 'May 3',  users: 10200 },
  { time: 'May 4',  users: 10800 },
];

const engagementData = [
  { name: 'Home',        value: 10 },
  { name: 'Shop',        value: 5  },
  { name: 'Sundries',    value: 11 },
  { name: 'Colleges',    value: 9  },
  { name: 'Jobs',        value: 12 },
  { name: 'Marketplace', value: 13 },
  { name: 'Lost&Found',  value: 8  },
  { name: 'Vehicles',    value: 14 },
];

// ✅ NEW: Active users per main section (donut chart data)
const sectionActivityData = [
  { name: 'Home', users: 2840, percentage: 25 },
  { name: 'Shops & Services', users: 1820, percentage: 16 },
  { name: 'Jobs', users: 1250, percentage: 11 },
  { name: 'Play to Win', users: 980, percentage: 9 },
  { name: 'My Wallet', users: 1420, percentage: 13 },
  { name: 'Featured Categories', users: 2970, percentage: 26 },
];

// ✅ NEW: Category browsing data for each main section
const categoryBrowsingData = {
  'Home': [
    { name: 'New Arrivals', users: 840 },
    { name: 'Trending', users: 720 },
    { name: 'Promotions', users: 650 },
    { name: 'Recommended', users: 630 },
  ],
  'Shops & Services': [
    { name: 'Verified Shops', users: 620 },
    { name: 'New Shops', users: 480 },
    { name: 'Top Rated', users: 420 },
    { name: 'Services', users: 300 },
  ],
  'Jobs': [
    { name: 'IT & Tech', users: 380 },
    { name: 'Sales & Marketing', users: 320 },
    { name: 'Healthcare', users: 280 },
    { name: 'Education', users: 270 },
  ],
  'Play to Win': [
    { name: 'Daily Challenges', users: 340 },
    { name: 'Leaderboard', users: 280 },
    { name: 'Rewards', users: 220 },
    { name: 'Tournaments', users: 140 },
  ],
  'My Wallet': [
    { name: 'Transactions', users: 520 },
    { name: 'Top Up', users: 380 },
    { name: 'Withdraw', users: 320 },
    { name: 'History', users: 200 },
  ],
  'Featured Categories': [
    { name: 'Electronics', users: 840 },
    { name: 'Property', users: 620 },
    { name: 'Vehicles', users: 580 },
    { name: 'Fashion & Beauty', users: 520 },
    { name: 'Furniture', users: 410 },
  ],
};

const SECTION_COLORS = ['#22D3EE', '#3B82F6', '#A78BFA', '#FB923C', '#34D399', '#F472B6'];
const CATEGORY_COLORS = ['#3B82F6','#8B5CF6','#EC4899','#F59E0B','#10B981','#6366F1','#14B8A6'];
const BAR_COLORS = ['#3B82F6','#FCD34D','#FB923C','#22D3EE','#34D399','#A5B4FC','#F87171','#2563EB'];

const trendingApps = [
  { name: 'Radiant Electro',   viewers: '3.2M', revenue: '$11,300', iconBg: '#DBEAFE', icon: '📱' },
  { name: 'Sedera Treasure',   viewers: '1.5M', revenue: '$11,300', iconBg: '#FED7AA', icon: '📦' },
  { name: 'BioConnect Guards', viewers: '1.1M', revenue: '$11,000', iconBg: '#DBEAFE', icon: '🏢' },
  { name: 'Radiant Splendida', viewers: '1.1M', revenue: '$10,657', iconBg: '#FED7AA', icon: '📦' },
];

// ─── Safely convert any value to a displayable string ────────────────────────
function safeStr(val) {
  if (val === null || val === undefined) return '0';
  if (typeof val === 'number') return val.toLocaleString();
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    for (const k of ['count','total','value','amount','num']) {
      if (val[k] !== undefined) return String(val[k]);
    }
    const num = Object.values(val).find(v => typeof v === 'number');
    return num !== undefined ? String(num) : '0';
  }
  return String(val);
}

// ─── Custom coloured bar shape ────────────────────────────────────────────────
const ColorBar = (props) => {
  const { x, y, width, height, index } = props;
  if (!height || height <= 0) return null;
  return (
    <rect x={x} y={y} width={width} height={height}
      fill={CATEGORY_COLORS[index % CATEGORY_COLORS.length]} rx={5} ry={5} />
  );
};

// ✅ NEW: Custom label for donut chart
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text 
      x={x} 
      y={y} 
      fill="white" 
      textAnchor={x > cx ? 'start' : 'end'} 
      dominantBaseline="central"
      fontSize="13"
      fontWeight="700"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
function DashboardView({ stats, recentActivity, products, setCurrentView, handleDeleteProduct }) {
  const [period, setPeriod] = useState('week');
  const [selectedSection, setSelectedSection] = useState('Featured Categories');

  // Build 4 stat cards
  const statCards = React.useMemo(() => {
    const base = [
      { icon: '📊', label: 'Total Registered Users',  bgColor: '#EBF3FF', iconColor: '#2563EB', key: ['total_users','totalUsers','users'] },
      { icon: '👥', label: 'New Registrations Today', bgColor: '#E0F2FE', iconColor: '#0EA5E9', key: ['new_users_today','newUsersToday','new_today'] },
      { icon: '👤', label: 'Current Active Users',    bgColor: '#D1FAE5', iconColor: '#10B981', key: ['active_users','activeUsers','active'] },
      { icon: '📦', label: 'Total Products',          bgColor: '#FEF3C7', iconColor: '#F59E0B', key: ['total_products','totalProducts','products'] },
    ];

    if (!stats) return base.map(b => ({ ...b, value: '0' }));
    if (Array.isArray(stats) && stats[0]?.label) {
      return stats.slice(0, 4).map(s => ({ ...s, value: safeStr(s.value) }));
    }
    if (!Array.isArray(stats)) {
      return base.map(b => {
        const raw = b.key.reduce((found, k) => found !== undefined ? found : stats[k], undefined);
        return { ...b, value: safeStr(raw) };
      });
    }
    return base.map(b => ({ ...b, value: '0' }));
  }, [stats]);

  // Activity list
  const activityList = React.useMemo(() => {
    if (!Array.isArray(recentActivity) || !recentActivity.length) return [];
    return recentActivity.slice(0, 5).map((a, i) => ({
      key:    a.id ?? i,
      avatar: a.avatar ?? a.icon ?? '👤',
      name:   a.name  ?? a.title ?? 'User',
      action: a.action ?? a.description ?? a.time_ago ?? '',
      color:  a.color ?? '#10B981',
      count:  a.count ?? '',
    }));
  }, [recentActivity]);

  const productsList = Array.isArray(products) ? products : [];
  const currentCategoryData = categoryBrowsingData[selectedSection] || [];

  return (
    <div className="dv-root">
      <h1 className="dv-title">Welcome Back, Admin!</h1>

      {/* Stat cards */}
      <div className="dv-stats">
        {statCards.map((s, i) => (
          <div key={i} className="dv-stat-card">
            <div className="dv-stat-icon" style={{ background: s.bgColor, color: s.iconColor }}>
              {s.icon}
            </div>
            <div>
              <div className="dv-stat-value">{s.value}</div>
              <div className="dv-stat-label">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Main body */}
      <div className="dv-body">
        <div className="dv-main">

          {/* Registrations over time */}
          <div className="dv-card">
            <div className="dv-card-top">
              <div>
                <h2 className="dv-card-title">Registrations Over Time</h2>
                <div className="dv-card-sub">
                  <span className="dv-badge">36</span>
                  <span className="dv-sub-text">New registrations this week</span>
                </div>
              </div>
              <div className="dv-controls">
                <span className="dv-legend"><span className="dv-dot" style={{background:'#93C5FD'}}/>Create Up</span>
                <span className="dv-legend"><span className="dv-dot" style={{background:'#3B82F6'}}/>Users</span>
                <select className="dv-select" value={period} onChange={e => setPeriod(e.target.value)}>
                  <option value="week">Past Week</option>
                  <option value="month">Past Month</option>
                  <option value="year">Past Year</option>
                </select>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={190}>
              <AreaChart data={registrationData} margin={{top:4,right:4,left:-20,bottom:0}}>
                <defs>
                  <linearGradient id="gU" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#3B82F6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="gC" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#93C5FD" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#93C5FD" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false}/>
                <XAxis dataKey="date" stroke="#9CA3AF" tick={{fontSize:11}}/>
                <YAxis stroke="#9CA3AF" tick={{fontSize:11}}/>
                <Tooltip/>
                <Area type="monotone" dataKey="createUp" stroke="#93C5FD" fill="url(#gC)"/>
                <Area type="monotone" dataKey="users" stroke="#3B82F6" strokeWidth={2} fill="url(#gU)"/>
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Two small charts */}
          <div className="dv-row2">
            <div className="dv-card">
              <h2 className="dv-card-title">Engagement by Section</h2>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={engagementData} margin={{top:4,right:4,left:-20,bottom:0}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false}/>
                  <XAxis dataKey="name" stroke="#9CA3AF" tick={{fontSize:9}}/>
                  <YAxis stroke="#9CA3AF" tick={{fontSize:10}}/>
                  <Tooltip/>
                  <Bar dataKey="value" shape={<ColorBar/>}/>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="dv-card">
              <div className="dv-inline-top">
                <h2 className="dv-card-title">Users Online Now</h2>
                <span className="dv-online-pill">
                  <span className="dv-pulse"/>5,403<span className="dv-plus"> +278</span>
                </span>
              </div>
              <ResponsiveContainer width="100%" height={160}>
                <AreaChart data={onlineData} margin={{top:4,right:4,left:-20,bottom:0}}>
                  <defs>
                    <linearGradient id="gO" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false}/>
                  <XAxis dataKey="time" stroke="#9CA3AF" tick={{fontSize:11}}/>
                  <YAxis stroke="#9CA3AF" tick={{fontSize:11}}/>
                  <Tooltip/>
                  <Area type="monotone" dataKey="users" stroke="#3B82F6" strokeWidth={2} fill="url(#gO)"/>
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ✅ NEW: Two side-by-side analytics charts */}
          <div className="dv-row2">
            {/* Left: Active Users by Section (Donut Chart) */}
            <div className="dv-card dv-analytics-card">
              <h2 className="dv-card-title">Active Users per Section</h2>
              <p className="dv-card-subtitle">Real-time distribution across main areas</p>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={sectionActivityData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius={90}
                    innerRadius={55}
                    fill="#8884d8"
                    dataKey="users"
                    paddingAngle={2}
                  >
                    {sectionActivityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={SECTION_COLORS[index % SECTION_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [`${value.toLocaleString()} users`, '']}
                    contentStyle={{
                      background: '#fff',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="dv-donut-legend">
                {sectionActivityData.map((item, i) => (
                  <div key={i} className="dv-legend-item">
                    <span className="dv-legend-dot" style={{ background: SECTION_COLORS[i] }}/>
                    <span className="dv-legend-label">{item.name}</span>
                    <span className="dv-legend-value">{item.users.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Category Browsing (Bar Chart with Section Toggle) */}
            <div className="dv-card dv-analytics-card">
              <div className="dv-card-top">
                <div>
                  <h2 className="dv-card-title">Category Browsing Activity</h2>
                  <p className="dv-card-subtitle">What users are viewing in each section</p>
                </div>
              </div>
              
              {/* Section toggle buttons */}
              <div className="dv-section-toggles">
                {sectionActivityData.map((section, i) => (
                  <button
                    key={i}
                    className={`dv-toggle-btn ${selectedSection === section.name ? 'active' : ''}`}
                    style={{
                      borderColor: selectedSection === section.name ? SECTION_COLORS[i] : '#E5E7EB',
                      background: selectedSection === section.name ? `${SECTION_COLORS[i]}15` : '#fff',
                      color: selectedSection === section.name ? SECTION_COLORS[i] : '#6B7280'
                    }}
                    onClick={() => setSelectedSection(section.name)}
                  >
                    {section.name}
                  </button>
                ))}
              </div>

              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={currentCategoryData} margin={{top:10,right:4,left:-20,bottom:0}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false}/>
                  <XAxis dataKey="name" stroke="#9CA3AF" tick={{fontSize:10}}/>
                  <YAxis stroke="#9CA3AF" tick={{fontSize:10}}/>
                  <Tooltip 
                    cursor={{ fill: '#F9FAFB' }}
                    contentStyle={{
                      background: '#fff',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '12px'
                    }}
                  />
                  <Bar dataKey="users" shape={<ColorBar/>} radius={[5, 5, 0, 0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

        </div>

        {/* Sidebar */}
        <div className="dv-sidebar">
          {/* Recent Activity */}
          <div className="dv-card">
            <h2 className="dv-card-title">Recent Activity</h2>
            {activityList.length === 0
              ? <p className="dv-empty">No recent activity</p>
              : (
                <div className="dv-act-list">
                  {activityList.map(a => (
                    <div key={a.key} className="dv-act-item">
                      <div className="dv-avatar">{a.avatar}</div>
                      <div className="dv-act-body">
                        <div className="dv-act-name">{a.name}</div>
                        <div className="dv-act-desc">{a.action}</div>
                      </div>
                      {a.count && (
                        <div className="dv-act-count" style={{color: a.color}}>{a.count}</div>
                      )}
                    </div>
                  ))}
                </div>
              )
            }
          </div>

          {/* Trending Apps */}
          <div className="dv-card">
            <h2 className="dv-card-title">Trending Apps</h2>
            <div className="dv-trend-list">
              {trendingApps.map((app, i) => (
                <div key={i} className="dv-trend-item">
                  <div className="dv-trend-icon" style={{background: app.iconBg}}>{app.icon}</div>
                  <div className="dv-trend-body">
                    <div className="dv-trend-name">{app.name}</div>
                    <div className="dv-trend-viewers">{app.viewers} viewers</div>
                  </div>
                  <div className="dv-trend-rev">{app.revenue}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Products Table */}
      {productsList.length > 0 && (
        <div className="dv-card dv-full-width-card">
          <div className="dv-card-top">
            <h2 className="dv-card-title">Recent Products</h2>
            <div className="dv-actions">
              <button className="dv-btn-sec">📊 Export</button>
              <button className="dv-btn-sec">🔍 Filter</button>
              {setCurrentView && (
                <button className="dv-btn-pri" onClick={() => setCurrentView('products')}>
                  + Add Product
                </button>
              )}
            </div>
          </div>
          <div className="dv-table-wrap">
            <table className="dv-table">
              <thead>
                <tr>
                  <th>PRODUCT</th><th>PRICE</th><th>STATUS</th>
                  <th>CATEGORY</th><th>DATE</th><th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {productsList.slice(0, 5).map(p => (
                  <tr key={p.id}>
                    <td>
                      <div className="dv-prod-cell">
                        {p.image && <img src={p.image} alt={p.title} className="dv-thumb"/>}
                        <span className="dv-prod-name">{p.title}</span>
                      </div>
                    </td>
                    <td className="dv-price">KES {Number(p.price||0).toLocaleString()}</td>
                    <td>
                      <span className={`dv-status-badge ${(p.status||'').toLowerCase()}`}>
                        {p.status}
                      </span>
                    </td>
                    <td>{p.category}</td>
                    <td>{p.date_added}</td>
                    <td>
                      <div className="dv-icon-row">
                        <button className="dv-icon-btn">✏️</button>
                        {handleDeleteProduct && (
                          <button className="dv-icon-btn"
                            onClick={() => handleDeleteProduct(p.id)}>🗑️</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardView;